/**
 * AddToPlaylistDialog — v3.0.134 (Phase 1 completion)
 *
 * Reusable dialog for adding one or many songs to an existing playlist,
 * or to a brand-new playlist that gets created inline. Used from:
 *   - SongDetail.jsx (single song, songIds = [songId])
 *   - Dashboard.jsx  (bulk multi-select, songIds = [...])
 *
 * Contract:
 *   props.open           boolean — controlled visibility
 *   props.onOpenChange   fn(boolean) — dismiss handler
 *   props.songIds        string[]    — song IDs to add
 *   props.onSuccess?     fn(playlist) — optional callback after adds
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ListMusic, Loader2, Music, Plus, Check, ExternalLink,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { apiFetch } from '../App';

export default function AddToPlaylistDialog({
  open, onOpenChange, songIds = [], onSuccess,
}) {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittingId, setSubmittingId] = useState(null); // playlist_id being written to
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [justAddedTo, setJustAddedTo] = useState(null); // for the ✓ flash

  const count = songIds.length;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch('/playlists');
      setPlaylists(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(`Couldn't load playlists: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setJustAddedTo(null);
      setShowCreateInput(false);
      setNewName('');
      load();
    }
  }, [open, load]);

  const addSongsTo = async (playlistId, playlistName) => {
    if (!songIds.length) return;
    setSubmittingId(playlistId);
    try {
      const { data: updated } = await apiFetch(
        `/playlists/${playlistId}/songs`,
        {
          method: 'POST',
          body: JSON.stringify({ song_ids: songIds }),
        },
      );
      const added = (updated?.songs || []).length - (playlists.find((p) => p.playlist_id === playlistId)?.song_count || 0);
      const label = count === 1
        ? `Added to "${playlistName}"`
        : `Added ${count} songs to "${playlistName}"`;
      toast.success(added > 0 ? label : `Already in "${playlistName}"`);
      setJustAddedTo(playlistId);
      // Update local song_count so the badge reflects the change
      setPlaylists((prev) =>
        prev.map((p) => (p.playlist_id === playlistId
          ? { ...p, song_count: (updated?.songs || []).length }
          : p)),
      );
      if (onSuccess) onSuccess(updated);
    } catch (err) {
      const msg = err.message || String(err);
      // Backend returns 400 when nothing new is added — treat as info.
      if (msg.toLowerCase().includes('no new songs')) {
        toast.info('All selected songs are already in this playlist');
        setJustAddedTo(playlistId);
      } else {
        toast.error(`Failed to add: ${msg}`);
      }
    } finally {
      setSubmittingId(null);
    }
  };

  const createAndAdd = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error('Give the new playlist a name');
      return;
    }
    setCreating(true);
    try {
      const { data: pl } = await apiFetch('/playlists', {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
      // Now push the songs into it
      if (songIds.length) {
        await apiFetch(`/playlists/${pl.playlist_id}/songs`, {
          method: 'POST',
          body: JSON.stringify({ song_ids: songIds }),
        });
      }
      toast.success(`Created "${pl.name}" and added ${count} song${count === 1 ? '' : 's'}`);
      setNewName('');
      setShowCreateInput(false);
      setJustAddedTo(pl.playlist_id);
      await load();
      if (onSuccess) onSuccess(pl);
    } catch (err) {
      toast.error(`Create failed: ${err.message || err}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-950 border-white/10 text-white max-w-md"
        data-testid="add-to-playlist-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListMusic className="text-emerald-400" size={20} />
            Add to Playlist
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {count === 0
              ? 'No songs selected.'
              : count === 1
                ? 'Pick a playlist for this song.'
                : `Pick a playlist for these ${count} songs.`}
          </DialogDescription>
        </DialogHeader>

        {/* Existing playlists list */}
        <div className="max-h-[45vh] overflow-y-auto -mx-1 px-1 space-y-1">
          {loading ? (
            <div className="py-8 text-center text-zinc-500 text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          ) : playlists.length === 0 ? (
            <div className="py-6 text-center text-sm text-zinc-500 border border-dashed border-white/10 rounded-md">
              No playlists yet — create your first one below.
            </div>
          ) : (
            playlists.map((p) => {
              const isBusy = submittingId === p.playlist_id;
              const wasAdded = justAddedTo === p.playlist_id;
              return (
                <button
                  key={p.playlist_id}
                  onClick={() => addSongsTo(p.playlist_id, p.name)}
                  disabled={isBusy || submittingId !== null}
                  data-testid={`add-to-playlist-${p.playlist_id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md border text-left transition-colors ${
                    wasAdded
                      ? 'bg-emerald-500/15 border-emerald-500/40'
                      : 'bg-white/5 hover:bg-white/10 border-white/5 hover:border-white/15'
                  } disabled:opacity-50 disabled:cursor-wait`}
                >
                  <Music size={16} className="text-zinc-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{p.name}</div>
                    <div className="text-[11px] text-zinc-500">
                      {p.song_count} song{p.song_count === 1 ? '' : 's'}
                    </div>
                  </div>
                  {isBusy ? (
                    <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
                  ) : wasAdded ? (
                    <Check className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <Plus className="w-4 h-4 text-zinc-500" />
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Create-new row */}
        <div className="border-t border-white/10 pt-3">
          {!showCreateInput ? (
            <Button
              variant="ghost"
              className="w-full justify-start text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200"
              onClick={() => setShowCreateInput(true)}
              data-testid="add-to-playlist-new-open"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Create a new playlist…
            </Button>
          ) : (
            <div className="flex gap-2">
              <Input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') createAndAdd();
                  if (e.key === 'Escape') {
                    setShowCreateInput(false);
                    setNewName('');
                  }
                }}
                placeholder="New playlist name"
                maxLength={120}
                className="flex-1 bg-black/40 border-white/10 text-white"
                data-testid="add-to-playlist-new-input"
              />
              <Button
                onClick={createAndAdd}
                disabled={creating || !newName.trim()}
                data-testid="add-to-playlist-new-confirm"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Create'
                )}
              </Button>
            </div>
          )}
          {justAddedTo && (
            <Link
              to={`/playlist/${justAddedTo}`}
              onClick={() => onOpenChange(false)}
              className="inline-flex items-center gap-1 mt-2 text-xs text-emerald-300 hover:underline"
              data-testid="add-to-playlist-view-link"
            >
              Open playlist <ExternalLink size={12} />
            </Link>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
